// ============================================================================
// Supply picker — searchable catalog browser. Hits public.supplies once,
// caches in component state. Filtering is client-side because the catalog
// is only ~227 rows.
//
// Used by:
//   • new-request modal manual entry
//   • the AI confirmation step, when the parser left supply_id null
// ============================================================================

import { useEffect, useMemo, useState } from 'react';
import { supabase } from '../../shared/supabase';
import {
  SUPPLY_CATEGORY_LABELS,
  type Supply,
  type SupplyCategoryCode,
} from '../../shared/types';

interface Props {
  value: string | null; // selected supply_id
  onChange: (supply: Supply | null) => void;
  // Optional: limit the picker to one category at a time. Useful when the
  // parser already classified the item but couldn't pick the exact SKU.
  initialCategory?: SupplyCategoryCode | null;
}

export default function SupplyPicker({
  value,
  onChange,
  initialCategory = null,
}: Props) {
  const [supplies, setSupplies] = useState<Supply[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState<SupplyCategoryCode | 'all'>(
    initialCategory ?? 'all',
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const { data, error: e } = await supabase
        .from('supplies')
        .select(
          'supply_id, item_name, sku, supply_category, sub_category, preferred_vendor, vendor_id, unit, unit_cost, reimbursable, is_capital_equipment',
        )
        .order('item_name');
      if (cancelled) return;
      if (e) {
        setError(e.message);
      } else {
        setSupplies((data ?? []) as Supply[]);
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return supplies.filter((s) => {
      if (category !== 'all' && s.supply_category !== category) return false;
      if (!term) return true;
      const haystack = [s.item_name, s.sku, s.sub_category]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(term);
    });
  }, [supplies, search, category]);

  const selected = useMemo(
    () => supplies.find((s) => s.supply_id === value) ?? null,
    [supplies, value],
  );

  return (
    <div>
      {selected ? (
        <div className="flex items-center justify-between rounded-md border border-st-ok-border bg-st-ok-bg px-3 py-2">
          <div className="min-w-0">
            <div className="truncate text-sm font-medium text-tcs-text">
              {selected.item_name}
            </div>
            <div className="text-xs text-tcs-text-muted">
              {selected.supply_id} · {selected.supply_category} ·{' '}
              {selected.unit ?? '—'}
            </div>
          </div>
          <button
            type="button"
            onClick={() => onChange(null)}
            className="ml-2 shrink-0 rounded border border-tcs-border-strong bg-tcs-raised px-2 py-1 text-xs text-tcs-text-secondary hover:bg-tcs-bg-muted"
          >
            Change
          </button>
        </div>
      ) : (
        <>
          <div className="flex gap-2">
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search supplies…"
              className="flex-1 rounded-md border border-tcs-border bg-tcs-field px-3 py-2 text-base focus:border-tcs-border-input focus:outline-none"
            />
            <select
              value={category}
              onChange={(e) =>
                setCategory(e.target.value as SupplyCategoryCode | 'all')
              }
              className="rounded-md border border-tcs-border bg-tcs-field px-2 py-2 text-sm"
            >
              <option value="all">All categories</option>
              {(Object.keys(SUPPLY_CATEGORY_LABELS) as SupplyCategoryCode[]).map(
                (k) => (
                  <option key={k} value={k}>
                    {SUPPLY_CATEGORY_LABELS[k]}
                  </option>
                ),
              )}
            </select>
          </div>

          {loading && (
            <p className="mt-2 text-xs text-tcs-text-muted">Loading catalog…</p>
          )}
          {error && (
            <p className="mt-2 text-xs text-rag-red">{error}</p>
          )}

          {!loading && !error && (
            <ul className="mt-2 max-h-72 overflow-y-auto rounded-md border border-tcs-border bg-tcs-raised">
              {filtered.slice(0, 200).map((s) => (
                <li key={s.supply_id}>
                  <button
                    type="button"
                    onClick={() => onChange(s)}
                    className="flex w-full items-center justify-between gap-2 border-b border-tcs-border px-3 py-2 text-left text-sm hover:bg-tcs-bg-muted"
                  >
                    <div className="min-w-0">
                      <div className="truncate font-medium text-tcs-text">
                        {s.item_name}
                      </div>
                      <div className="truncate text-xs text-tcs-text-muted">
                        {s.supply_id} · {s.sub_category ?? s.supply_category}
                      </div>
                    </div>
                    <div className="shrink-0 text-right text-xs text-tcs-text-muted">
                      <div className="font-mono">{s.unit ?? '—'}</div>
                      {typeof s.unit_cost === 'number' && (
                        <div>${s.unit_cost.toFixed(2)}</div>
                      )}
                    </div>
                  </button>
                </li>
              ))}
              {filtered.length === 0 && (
                <li className="p-4 text-center text-sm text-tcs-text-muted">
                  No matches.
                </li>
              )}
              {filtered.length > 200 && (
                <li className="p-2 text-center text-xs text-tcs-text-muted">
                  Showing first 200. Refine the search to see more.
                </li>
              )}
            </ul>
          )}
        </>
      )}
    </div>
  );
}
